'use client';
import { Turnstile } from '@marsidev/react-turnstile';
import { useMask } from '@react-input/mask';
import {
  generateLeadURL,
  getCookieGoogleId,
  getCookieMsclkid
} from 'app/actions/affiliate.actions';
import { siteVerification } from 'app/actions/cloudflare.actions';
import { getTurnstileKey } from 'app/actions/turnstile.actions';
import { ButtonGreen } from 'components/buttons/default-green';
import { Modal, TextInput } from 'flowbite-react';
import { useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

interface DynamicForm {
  [key: string]: string;
}

export function PromotionBanner2({
  policy,
  sourceKey,
  phoneSourceKey
}: {
  heading: string;
  policy: string;
  sourceKey: string;
  phoneSourceKey: string;
}) {
  const searchParams = useSearchParams();
  const [isOpen, setIsOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedData = localStorage.getItem('hasShownPromoBanner');
      if (storedData) {
        const { timestamp } = JSON.parse(storedData);
        // Check if 24 hours have passed (86400000 milliseconds)
        if (Date.now() - timestamp < 86400000) {
          return false;
        }
      }
      if (window.innerWidth < 768) {
        localStorage.setItem(
          'hasShownPromoBanner',
          JSON.stringify({
            timestamp: Date.now()
          })
        );
        return true;
      }
    }
    return false;
  });
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [turnstileLoaded, setTurnstileLoaded] = useState<boolean>(false);
  const [turnstileKey, setTurnstileKey] = useState<string | null>(null);
  const [form, setForm] = useState<DynamicForm>({
    gaMedium: 'referral',
    gaSource: '',
    gaContent: '/',
    gaClientId: '970385394.1727054325',
    gaCampaign: '(referral)'
  });
  const [showPromotionalModal, setShowPromotionalModal] = useState(false);
  const [formError, setFormError] = useState<string>('');
  const emailInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);

  const inputRef = useMask({
    mask: '(___) ___-____',
    replacement: { _: /\d/ }
  });

  useEffect(() => {
    setTurnstileKey(getTurnstileKey());
  }, []);

  useEffect(() => {
    if (isOpen && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  async function createQuote(turnstileToken: string) {
    if (!turnstileToken) {
      setFormError('Please complete the verification');
      return;
    }

    if (!email) {
      setFormError('Please enter your email address');
      return;
    }

    try {
      const status = await siteVerification(turnstileToken);
      let gclid = await getCookieGoogleId();
      let msclkid = await getCookieMsclkid();

      if (status === 200) {
        const payload = {
          contact: {
            isResidential: true,
            email
          },
          compliance: {
            tcpaPermissionGranted: true,
            tcpaPermissionLink: ''
          },
          additionalFields: {
            EMAIL: email,
            submissionURL: window.location.href,
            ...form
          } as any
        };

        const subid = searchParams?.get('subid');
        if (subid) {
          payload.additionalFields['subid'] = subid;
        }

        if (gclid !== undefined) payload.additionalFields['googleclickid'] = gclid;
        if (msclkid !== undefined) payload.additionalFields['msclkid'] = msclkid;
        const leadURL = await generateLeadURL(sourceKey);

        if (leadURL) {
          const response = await fetch(leadURL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
          });
          if (response.status === 200) {
            setIsOpen(false);
            setTurnstileLoaded(false);
            setEmail('');
            setFormError('');
            setShowPromotionalModal(true);
          }
        }
      }
    } catch (e) {
      setFormError('An error occurred. Please try again.');
      console.log(e);
    }
  }

  const submitPhone = async () => {
    if (!phone) {
      setFormError('Please enter your phone number');
      return;
    }

    try {
      let gclid = await getCookieGoogleId();
      let msclkid = await getCookieMsclkid();

      const payload = {
        contact: {
          isResidential: true,
          email,
          phone: {
            number: phone
          }
        },
        compliance: {
          tcpaPermissionGranted: true,
          tcpaPermissionLink: ''
        },
        additionalFields: {
          EMAIL: email,
          PHONE: phone,
          submissionURL: window.location.href,
          ...form
        } as any
      };

      const subid = searchParams?.get('subid');
      if (subid) {
        payload.additionalFields['subid'] = subid;
      }

      if (gclid !== undefined) payload.additionalFields['googleclickid'] = gclid;
      if (msclkid !== undefined) payload.additionalFields['msclkid'] = msclkid;
      const leadURL = await generateLeadURL(phoneSourceKey);

      if (leadURL) {
        const response = await fetch(leadURL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        if (response.status === 200) {
          setEmail('');
          setPhone('');
          setFormError('');
          setShowPromotionalModal(false);
        }
      }
    } catch (e) {
      setFormError('An error occurred. Please try again.');
      console.log(e);
    }
  };

  return (
    <div
      className="flex flex-col items-center justify-between bg-fp-green py-2 lg:flex-row"
      role="banner"
      aria-label="Promotional offer"
    >
      <div className="flex h-full flex-col justify-center text-white">
        <button
          className="h-[33px] text-nowrap rounded-lg border border-white bg-fp-green px-4 hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-white"
          onClick={() => setIsOpen(true)}
          onKeyDown={(e) => handleKeyDown(e, () => setIsOpen(true))}
          aria-label="Get promotional offer"
        >
          Get Offer
        </button>
      </div>

      <Modal
        show={isOpen}
        size="lg"
        onClose={() => {
          setIsOpen(false);
          setTurnstileLoaded(false);
          setEmail('');
          setFormError('');
        }}
        aria-labelledby="offer-modal-title"
      >
        <Modal.Header className="!absolute right-0 z-10 border-none" />
        <Modal.Body>
          <div className="my-4 flex flex-col items-center gap-4">
            <h2 id="offer-modal-title" className="text-center text-lg font-bold">
              Enter Your Email to Get an <span className="text-fp-green">Offer</span>
            </h2>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (email && turnstileKey) {
                  createQuote(turnstileKey);
                }
              }}
            >
              <div className="w-full">
                <div className="flex w-full flex-row items-center rounded-lg border border-gray-300 bg-white">
                  <input
                    ref={emailInputRef}
                    type="email"
                    className="h-[41px] w-[210px] rounded-l-md border-none text-black"
                    name="email"
                    value={email}
                    placeholder="Email address"
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    aria-required="true"
                    aria-invalid={!!formError}
                    aria-describedby={formError ? 'email-error' : undefined}
                  />
                  <button
                    type="submit"
                    className="h-[40px] text-nowrap rounded-lg border border-fp-green bg-fp-green px-4 text-white hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-fp-green disabled:opacity-50"
                    disabled={!email || !turnstileLoaded}
                    aria-label="Submit email for offer"
                  >
                    Submit
                  </button>
                </div>
                {formError && (
                  <div id="email-error" className="mt-2 text-sm text-red-600" role="alert">
                    {formError}
                  </div>
                )}
              </div>
            </form>

            <div
              className="text-left text-[10px]"
              dangerouslySetInnerHTML={{
                __html: policy
              }}
              aria-label="Terms and conditions"
            />

            {turnstileKey && (
              <div aria-label="Verification">
                <Turnstile
                  siteKey={turnstileKey}
                  onSuccess={(e) => {
                    setTurnstileLoaded(true);
                    setFormError('');
                  }}
                />
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={showPromotionalModal}
        size="lg"
        onClose={() => setShowPromotionalModal(false)}
        aria-labelledby="promo-modal-title"
      >
        <Modal.Header className="!absolute right-0 z-10 border-none" />
        <Modal.Body>
          <div className="flex flex-col items-center gap-4">
            <h2 id="promo-modal-title" className="text-sm">
              Thank You! Your email with offer details is on the way.
            </h2>
            <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-black p-5">
              <div className="text-lg font-bold">
                Use Code <span className="text-fp-green">SAVE25</span>
              </div>
              <div className="text-sm font-bold">
                At Checkout for $25 OFF Your Purchase of $250 or More{' '}
              </div>
              <div className="text-sm">Use Your Offer to Buy Now Online</div>
              <ButtonGreen
                href="/shop"
                className="rounded-lg bg-fp-green p-2 text-white hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-fp-green"
                aria-label="Shop security systems"
              >
                Shop Systems
              </ButtonGreen>
            </div>
            <div className="text-sm">
              OR Talk to a Security Specialist to Unlock Further Savings
            </div>
            <div className="flex w-72 flex-row gap-2">
              <TextInput
                pattern="\([0-9]{3}\) [0-9]{3}-[0-9]{4}"
                placeholder="Enter your phone number"
                ref={inputRef}
                name="phone"
                type="text"
                required
                shadow
                className="w-full"
                onChange={(e) => setPhone(e.target.value)}
                aria-required="true"
                aria-invalid={!!formError}
                aria-describedby={formError ? 'phone-error' : undefined}
              />
              <button
                className="rounded-lg bg-fp-green p-2 text-white hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-fp-green"
                onClick={submitPhone}
                aria-label="Submit phone number"
              >
                Submit
              </button>
            </div>
            {formError && (
              <div id="phone-error" className="text-sm text-red-600" role="alert">
                {formError}
              </div>
            )}
            <div className="text-sm">Or Call Us</div>
            <ButtonGreen
              href="tel:844-759-3009"
              className="rounded-lg bg-fp-green p-2 text-white hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-fp-green"
              aria-label="Call 844-759-3009"
            >
              <div className="flex flex-row items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="size-6"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M1.5 4.5a3 3 0 0 1 3-3h1.372c.86 0 1.61.586 1.819 1.42l1.105 4.423a1.875 1.875 0 0 1-.694 1.955l-1.293.97c-.135.101-.164.249-.126.352a11.285 11.285 0 0 0 6.697 6.697c.103.038.25.009.352-.126l.97-1.293a1.875 1.875 0 0 1 1.955-.694l4.423 1.105c.834.209 1.42.959 1.42 1.82V19.5a3 3 0 0 1-3 3h-2.25C8.552 22.5 1.5 15.448 1.5 6.75V4.5Z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="ml-2">844-759-3009</span>
              </div>
            </ButtonGreen>
            <div className="w-full pt-4 text-justify">
              <p className="text-[12px]">
                By submitting your phone number, you authorize Frontpoint to send you marketing
                communications by phone. Calls may be made using an automatic telephone dialing
                system and may play a recorded message when a connection is complete or transmit a
                pre-recorded voicemail.
              </p>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
